import type { Dict } from "./i18n";
import type { SettingsTab } from "./route";

/**
 * Settings, grouped the way people look for them: first what is about you,
 * then what the customer sees, then the team, then the bill.
 *
 * The table lives here rather than in SettingsView so the visibility rules can
 * be tested without rendering a screen. Two of them:
 *
 * - `adminOnly` — owners and admins (team, audit log).
 * - `ownerOnly` — the owner alone. Billing spends money and the server refuses
 *   every billing route to anyone else (403), so an admin must not even see
 *   the tab.
 */

export type TabDef = { key: SettingsTab; label: (t: Dict) => string; adminOnly?: boolean; ownerOnly?: boolean };
export type TabGroup = { title: (t: Dict) => string; tabs: TabDef[] };

export interface TabAccess {
  /** Owner or admin. */
  isAdmin: boolean;
  isOwner: boolean;
}

export const SETTINGS_GROUPS: TabGroup[] = [
  {
    title: (t) => t.settings.groupAccount,
    tabs: [
      { key: "profile", label: (t) => t.settings.tabProfile },
      { key: "notifications", label: (t) => t.settings.tabNotifications },
      { key: "security", label: (t) => t.settings.tabSecurity },
    ],
  },
  {
    title: (t) => t.settings.groupWidget,
    tabs: [
      { key: "widget", label: (t) => t.settings.tabWidget },
      { key: "canned", label: (t) => t.settings.tabCanned },
      { key: "triggers", label: (t) => t.settings.tabTriggers },
      { key: "hours", label: (t) => t.settings.tabHours },
      { key: "spam", label: (t) => t.settings.tabSpam },
    ],
  },
  {
    title: (t) => t.settings.groupAdmin,
    tabs: [
      { key: "sites", label: (t) => t.settings.tabSites },
      { key: "operators", label: (t) => t.settings.tabOperators, adminOnly: true },
      { key: "audit", label: (t) => t.settings.tabAudit, adminOnly: true },
      { key: "billing", label: (t) => t.settings.tabBilling, ownerOnly: true },
    ],
  },
];

export function accessOf(role: "owner" | "admin" | "agent"): TabAccess {
  return { isAdmin: role === "owner" || role === "admin", isOwner: role === "owner" };
}

export function canSeeTab(tab: TabDef, access: TabAccess): boolean {
  if (tab.ownerOnly && !access.isOwner) return false;
  if (tab.adminOnly && !access.isAdmin) return false;
  return true;
}

export function visibleTabs(access: TabAccess): SettingsTab[] {
  return SETTINGS_GROUPS.flatMap((group) => group.tabs.filter((tab) => canSeeTab(tab, access)).map((tab) => tab.key));
}

/**
 * The tab that actually renders. A link to one this operator may not open
 * lands on their profile instead of on an empty screen or a 403.
 */
export function guardTab(tab: SettingsTab, access: TabAccess): SettingsTab {
  const def = SETTINGS_GROUPS.flatMap((group) => group.tabs).find((x) => x.key === tab);
  return def && canSeeTab(def, access) ? tab : "profile";
}
